import { DateTime } from 'luxon'

export const REQUEST_STATUS = {
  PURCHASED: 'PURCHASED',
  READY_TO_ANALYZE: 'READY_TO_ANALYZE',
  COMPLETE: 'COMPLETE',
}

export const REQUEST_TABS = {
  PURCHASED: 'purchased',
  READY: 'ready',
  COMPLETE: 'complete',
}

const TAB_BY_STATUS = {
  [REQUEST_STATUS.PURCHASED]: REQUEST_TABS.PURCHASED,
  [REQUEST_STATUS.READY_TO_ANALYZE]: REQUEST_TABS.READY,
  [REQUEST_STATUS.COMPLETE]: REQUEST_TABS.COMPLETE,
}

export const STATUS_LABELS = {
  [REQUEST_STATUS.PURCHASED]: 'Purchased',
  [REQUEST_STATUS.READY_TO_ANALYZE]: 'Ready to analyze',
  [REQUEST_STATUS.COMPLETE]: 'Complete',
}

export const isRequestTab = value => Object.values(REQUEST_TABS).includes(value)

export const partitionRequestsByTab = (requests = []) =>
  requests.reduce(
    (acc, request) => {
      const tab = TAB_BY_STATUS[request.status]
      if (tab) acc[tab].push(request)
      return acc
    },
    {
      [REQUEST_TABS.PURCHASED]: [],
      [REQUEST_TABS.READY]: [],
      [REQUEST_TABS.COMPLETE]: [],
    },
  )

export const isOverdue = (request, now = DateTime.local()) => {
  if (request?.status !== REQUEST_STATUS.READY_TO_ANALYZE || !request.dueAt) {
    return false
  }
  return DateTime.fromISO(request.dueAt) < now
}

export const countOverdue = (requests = [], now = DateTime.local()) =>
  requests.filter(request => isOverdue(request, now)).length

export const getStudentName = student =>
  [student?.firstName, student?.lastName].filter(Boolean).join(' ') || 'Student'

export const formatDate = isoString =>
  isoString ? DateTime.fromISO(isoString).toFormat('LLL d, yyyy') : '—'

export const formatDateTime = isoString =>
  isoString
    ? DateTime.fromISO(isoString).toFormat("LLL d, yyyy 'at' h:mm a")
    : '—'

// Describes time left (or overdue) against the coach's turnaround commitment.
export const getAgingLabel = (request, now = DateTime.local()) => {
  if (!request?.dueAt) return '—'

  const due = DateTime.fromISO(request.dueAt)
  const diffHours = Math.round(Math.abs(due.diff(now, 'hours').hours))
  const amount =
    diffHours >= 48
      ? `${Math.round(diffHours / 24)} days`
      : `${Math.max(diffHours, 1)} ${diffHours === 1 ? 'hour' : 'hours'}`

  return due < now ? `${amount} overdue` : `${amount} left`
}

// Hours between the golfer submitting and the coach sharing the response.
export const getTurnaroundHours = request => {
  if (!request?.submittedAt || !request?.completedAt) return null
  const hours = DateTime.fromISO(request.completedAt).diff(
    DateTime.fromISO(request.submittedAt),
    'hours',
  ).hours
  return Math.max(Math.round(hours), 0)
}

export const formatTurnaroundDuration = hours => {
  if (hours == null) return '—'
  if (hours >= 48) return `${Math.round(hours / 24)} days`
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
}

export const getRequestPath = requestId =>
  `/pga-coach/virtual-coaching/${requestId}`

export const getRequestsPath = tab =>
  tab ? `/pga-coach/virtual-coaching?tab=${tab}` : '/pga-coach/virtual-coaching'

const ONFORM_HOSTS = [
  'link.getonform.com',
  'getonform.com',
  'www.getonform.com',
]

export const isOnformUrl = value => {
  try {
    const url = new URL(value.trim())
    return (
      url.protocol === 'https:' &&
      ONFORM_HOSTS.some(
        host => url.hostname === host || url.hostname.endsWith(`.${host}`),
      )
    )
  } catch (error) {
    return false
  }
}

export const isHttpsUrl = value => {
  try {
    return new URL(value).protocol === 'https:'
  } catch (error) {
    return false
  }
}

// Browsers ignore `download` on cross-origin links. Cloudinary can serve an
// attachment copy of a video with the fl_attachment flag instead.
export const getDownloadUrl = value => {
  try {
    const url = new URL(value)
    if (
      url.hostname === 'res.cloudinary.com' &&
      url.pathname.includes('/upload/') &&
      !url.pathname.includes('/fl_attachment')
    ) {
      url.pathname = url.pathname.replace('/upload/', '/upload/fl_attachment/')
      return url.toString()
    }
  } catch (error) {
    // Fall through to the original URL
  }
  return value
}

export const ONFORM_APP_URL = 'https://www.getonform.com'

// Props for every Virtual Coaching <video controls>. They remove the browser's
// overflow menu (Download / Playback speed / Picture in Picture), which can't
// be styled; we render our own Download buttons.
export const VIDEO_PLAYER_PROPS = {
  controlsList: 'nodownload noplaybackrate',
  disablePictureInPicture: true,
}

export const formatFileSize = bytes => {
  if (bytes == null) return ''
  if (bytes < 1024 * 1024) return `${Math.max(Math.round(bytes / 1024), 1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
